import React, { RefObject } from 'react';

import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { UseFormSetValue } from 'react-hook-form';

import ActualOperations from '../../pages/actualOperations/ActualOperations';
import {
  ActualOperation,
  DropdownObjectType,
  Employee,
  OperationAttainment,
  Task,
  TreeNode,
  TreeNodeBase
} from '../../types/interfaces';
import { CustomDropdown } from '../../ui/custom-controls';
import { PickerTexts } from '../../utils';
import type { WorkOrderTimeframe } from '../../utils/attainmentAvailability';
import { markDeletedItems } from '../../utils/markDeletedItems';
import { TagsComponentWithUnderline } from '../TagsComponentWithUnderline';

interface OperationTabContentProps {
  plannedOperations: DropdownObjectType[];
  plannedOperationsOptions: TreeNodeBase[];
  plannedOperationsAttainments: OperationAttainment[];
  allOperationsAttainments?: OperationAttainment[];
  actualOperationsEmployeeOptions?: Employee[];
  setValue: UseFormSetValue<Task>;
  actualOperations: ActualOperation[];
  handleDeletePlannedJob: (id: string | number) => void;
  triggerApiCall?: number;
  tagScrollHeight: number;
  employees: any[];
  rawEmployeesData?: Employee[];
  tagScrollRef: RefObject<HTMLDivElement>;
  loading: boolean;
  department?: TreeNode;
  plannedOperationsErrorMessage?: string;
  actualOperationsErrorMessage?: string;
  workOrderTimeframe?: WorkOrderTimeframe;
}

const OperationTabContent: React.FC<OperationTabContentProps> = ({
  plannedOperations,
  plannedOperationsOptions,
  plannedOperationsAttainments,
  allOperationsAttainments,
  actualOperationsEmployeeOptions,
  setValue,
  actualOperations,
  handleDeletePlannedJob,
  tagScrollHeight,
  employees,
  rawEmployeesData,
  tagScrollRef,
  loading,
  department,
  plannedOperationsErrorMessage,
  actualOperationsErrorMessage,
  workOrderTimeframe
}) => {
  return (
    <>
      {loading ? (
        <ProgressSpinner />
      ) : (
        <div className="task-card__column flex flex-column mr-4">
          <div className="flex flex-column mb-2">
            <div className="flex justify-content-between align-items-center gap-3 flex-shrink-0 task-card__planned-ops-row">
              <label
                style={{ padding: '0' }}
                className="fixed-labels"
                htmlFor="plannedOperations"
              >
                Плановые работы:
              </label>

              <div style={{ width: '100%' }}>
                <CustomDropdown<TreeNodeBase>
                  multiplySelectMode
                  assetOptions={plannedOperationsOptions}
                  selectedAsset={plannedOperations as TreeNodeBase[]}
                  onSelectionChange={(e) => {
                    const selectedValues = e as TreeNodeBase[];
                    if (selectedValues)
                      setValue('plannedOperations', selectedValues, {
                        shouldDirty: true,
                        shouldValidate: true
                      });
                  }}
                  placeholder={PickerTexts.plannedWorks(
                    plannedOperations.length
                  )}
                  columns={[
                    { field: 'label' as any, header: 'Работа', width: '40%' },
                    {
                      field: 'attainments' as any,
                      header: 'Требуемые навыки и допуски',
                      width: '40%',
                      body: (row: any) => {
                        const attainments = row.attainments ?? [];
                        if (!attainments.length)
                          return (
                            <div className="flex items-center justify-center text-gray-500 italic text-sm py-2">
                              Навыки или допуски не требуются
                            </div>
                          );

                        return (
                          <div
                            style={{
                              display: 'flex',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              alignItems: 'center',
                              maxWidth: '100%'
                            }}
                            title={attainments
                              .map((a: any) => a.name)
                              .join(', ')} // тултип со всеми навыками
                          >
                            {attainments.map((a: any) => (
                              <span
                                key={a.id}
                                style={{
                                  backgroundColor: '#e2e8f0',
                                  color: '#1a202c',
                                  padding: '2px 6px',
                                  borderRadius: '8px',
                                  fontSize: '12px',
                                  marginRight: '4px',
                                  flexShrink: 0
                                }}
                              >
                                {a.shortName || a.name}
                              </span>
                            ))}
                          </div>
                        );
                      }
                    },
                    {
                      field: 'effort' as any,
                      header: 'Плановые трудозатраты, мин',
                      width: '20%'
                    }
                  ]}
                  emptyMessage="Нет доступных работ."
                  overlayWidthMultiplier={1}
                  dialogCancelationWarn={true}
                  showDeletedWarning
                  deletedWarningTooltip="Некоторые плановые работы удалены в справочнике"
                />
              </div>
            </div>
          </div>

          <div
            ref={tagScrollRef}
            id="tags-scroll"
            className="task-card--tag-scroll task-card__planned-ops-scroll"
          >
            {plannedOperations?.length > 0 ? (
              <TagsComponentWithUnderline
                passedClassName="gap-2"
                actualOperations={actualOperations}
                tagsItems={
                  markDeletedItems(
                    plannedOperations,
                    plannedOperationsOptions
                  ) as DropdownObjectType[]
                }
                onDelete={handleDeletePlannedJob}
                plannedOperationsAttainments={plannedOperationsAttainments}
                withTooltip
              />
            ) : plannedOperationsErrorMessage ? (
              <Message
                className="w-full mt-2"
                severity="error"
                text={plannedOperationsErrorMessage}
              />
            ) : (
              <label
                style={{ padding: '0', marginLeft: '1rem' }}
                className="col-fixed fixed-labels"
              >
                Плановые работы не выбраны
              </label>
            )}
          </div>

          {actualOperationsErrorMessage ? (
            <Message
              className="w-full mb-2"
              severity="error"
              text={actualOperationsErrorMessage}
            />
          ) : null}

          <ActualOperations
            actualOperations={actualOperations}
            setActualOperations={(e) => {
              if (Array.isArray(e)) {
                setValue('actualOperations', e as ActualOperation[], {
                  shouldDirty: true,
                  shouldValidate: true
                });
              }
            }}
            tagScrollHeight={tagScrollHeight}
            employees={actualOperationsEmployeeOptions || employees}
            rawEmployeesData={
              actualOperationsEmployeeOptions || rawEmployeesData
            }
            plannedOperations={plannedOperations}
            plannedOperationsAttainments={plannedOperationsAttainments}
            allOperationsAttainments={allOperationsAttainments}
            plannedOperationsOptions={plannedOperationsOptions}
            department={department}
            workOrderTimeframe={workOrderTimeframe}
          />
        </div>
      )}
    </>
  );
};

export default OperationTabContent;
